"use client"

import React from "react"
import {ArrowUpRight, Box, ChevronDown, ChevronRight, Eye, Loader2} from "lucide-react"
import {
  CallNoteRecord,
  IssueTracker,
  type CallNoteMetadata,
  type InsightEvidence,
  type ResolvedChangeArtifact,
  type Signal,
  type SignalArtifact,
} from "@devplaninc/codeup"
import {cn} from "@/lib/utils"
import {formatCompactRelativeTime, formatRelativeTime} from "@/app/util/relativeTime"
import {Markdown} from "@/app/components/messages/Markdown"
import {commitUrl, prUrl, repoProvider} from "@/app/util/gitUtil"
import {companyCallNoteByProviderRefAPI} from "@/app/apiPaths"
import {jr} from "@/hooks/queries/response-handler"
import {
  GetCallNoteByProviderRefRequest,
  GetCallNoteByProviderRefResponse,
} from "@/pb/api/devplan/services/web/company"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  callNoteProviderKey,
  collectSignalCallNoteEvidence,
  collectSignalDocumentEvidence,
  collectSignalIssueEvidence,
  issueEvidenceKey,
} from "@/app/components/evidence/evidenceHydrationRefs"
import {DetailFlyout} from "@/app/components/evidence/DetailFlyout"
import {EvidenceProviderGlyph, EvidenceProviderIcon} from "@/app/components/evidence/EvidenceProviderIcon"
import {artifactEvidenceProvider, evidenceProviderLabel, type EvidenceProvider} from "@/app/components/evidence/evidenceProvider"
import {
  artifactProviderLabel,
  callNoteTitle,
  artifactReferenceHref,
  artifactReferenceText,
  discussionTopicArtifactProvider,
} from "@/app/components/signals/signalKinds"
import {catalogueFeatureHref, catalogueFeatureLabel} from "@/app/util/catalogueFeatureUtil"
import {
  displayableEvidence,
  evidenceItemProviders,
  gitProviderEvidenceProvider,
  mergeEvidenceResolvers,
  toEvidenceDisplay,
  type EvidenceDisplay,
  type EvidenceResolvers,
} from "@/app/components/evidence/evidenceDisplay"
import {useEvidenceHydration} from "@/hooks/queries/use-evidence-hydration"
import {useCompanyFeatureFlag} from "@/hooks/queries/use-feature-flags"
import {slackHrefFromPermalink} from "@/app/components/evidence/slackEvidenceLinks"
import {FEATURE_RELATIONSHIP_LABELS} from "@/app/components/changes-report/ChangeMetadataBadges"

export interface EvidenceDetailFlyoutProps {
  evidence: InsightEvidence[]
  open: boolean
  onClose: () => void
  /** Header title when the evidence spans multiple providers. */
  title?: string
  /** Enables on-open hydration of referenced entities. */
  companyId?: number
  /** Pre-hydrated lookup data merged under the hydration results. */
  resolvers?: EvidenceResolvers
}

/**
 * Right-side flyout listing evidence references: source icon on the left,
 * the rationale and origin link on the right, captured detail behind a
 * per-row Details toggle. The single detail surface behind every evidence
 * pill.
 */
export function EvidenceDetailFlyout(props: EvidenceDetailFlyoutProps) {
  if (props.companyId === undefined) {
    return <EvidenceDetailFlyoutContent {...props} hydratedResolvers={undefined} loading={false}/>
  }
  return <HydratedEvidenceDetailFlyout {...props} companyId={props.companyId}/>
}

function HydratedEvidenceDetailFlyout(props: EvidenceDetailFlyoutProps & {companyId: number}) {
  const {resolvers, loading} = useEvidenceHydration(props.companyId, props.open ? props.evidence : [])
  const signalSourceEvidence = React.useMemo(() => {
    const signals = referencedEvidenceSignals(props.evidence, resolvers)
    return [
      ...collectSignalCallNoteEvidence(signals),
      ...collectSignalDocumentEvidence(signals),
      ...collectSignalIssueEvidence(signals),
    ]
  }, [props.evidence, resolvers])
  const {resolvers: sourceResolvers, loading: sourceLoading} = useEvidenceHydration(
    props.companyId,
    props.open ? signalSourceEvidence : [],
  )
  const slackNativeDeepLinks = useCompanyFeatureFlag(ff => ff.wsSlackNativeDeepLinks, props.companyId)
  const hydratedResolvers = React.useMemo(
    () => ({...mergeEvidenceResolvers(resolvers, sourceResolvers), slackNativeDeepLinks}),
    [resolvers, slackNativeDeepLinks, sourceResolvers],
  )
  return <EvidenceDetailFlyoutContent {...props} hydratedResolvers={hydratedResolvers} loading={loading || sourceLoading}/>
}

function referencedEvidenceSignals(evidence: InsightEvidence[], resolvers: EvidenceResolvers): Signal[] {
  const out: Signal[] = []
  const seenSignals = new Set<string>()
  const seenInsights = new Set<string>()

  const visit = (items: InsightEvidence[]) => {
    for (const item of items) {
      const target = item.ref?.target
      if (target?.$case === "signalId") {
        if (seenSignals.has(target.value)) continue
        const signal = resolvers.signals?.[target.value]
        if (!signal) continue
        seenSignals.add(target.value)
        out.push(signal)
      } else if (target?.$case === "insightId") {
        if (seenInsights.has(target.value)) continue
        seenInsights.add(target.value)
        const insight = resolvers.insights?.[target.value]
        if (insight) visit(insight.evidence)
      }
    }
  }

  visit(evidence)
  return out
}

interface DetailItem {
  evidence: InsightEvidence
  display: EvidenceDisplay
  provider: EvidenceProvider
  /** All underlying sources (change artifacts, signal artifacts), most frequent first. */
  providers: EvidenceProvider[]
}

export interface EvidenceDetailListProps {
  evidence: InsightEvidence[]
  resolvers?: EvidenceResolvers
}

export function EvidenceDetailList({evidence, resolvers}: EvidenceDetailListProps) {
  const slackNativeDeepLinks = useCompanyFeatureFlag(ff => ff.wsSlackNativeDeepLinks, resolvers?.companyId)
  const listResolvers = React.useMemo(
    () => resolvers?.companyId === undefined ? resolvers : {...resolvers, slackNativeDeepLinks},
    [resolvers, slackNativeDeepLinks],
  )
  const items = React.useMemo(
    () => detailItems(evidence, listResolvers),
    [evidence, listResolvers],
  )
  if (items.length === 0) return null

  return (
    <div>
      {items.map((item, index) => (
        <EvidenceDetailRow
          key={`evidence-entry-${index}`}
          item={item}
          resolvers={listResolvers ?? {}}
        />
      ))}
    </div>
  )
}

/** Rich evidence details for a parent-owned accordion row. */
export function EvidenceInlineDetails({
  evidence,
  resolvers,
  showBody = true,
}: EvidenceDetailListProps & {showBody?: boolean}) {
  const item = detailItems(evidence, resolvers)[0]
  if (!item) return null

  const {display} = item
  const body = display.body?.trim()
  const reference = display.referenceText?.trim()
  const note = display.note?.trim()
  const title = note || reference || evidenceProviderLabel(item.provider)
  const metaRows = detailMetaRows(display)
  const linkedItems = display.linkedItems ?? []
  const showInlineBody = Boolean(showBody && body && body !== title)
  const sourceLabel = display.referenceLabel ?? display.label ?? "source"
  if (!showInlineBody && metaRows.length === 0 && linkedItems.length === 0 && !display.href) return null

  return (
    <div className="space-y-4 px-5 py-4 pl-[4.25rem]">
      {showInlineBody && (
        <div className="prose prose-sm max-w-none text-[13px] leading-6 dark:prose-invert">
          <Markdown content={body ?? ""}/>
        </div>
      )}
      {metaRows.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
          {metaRows.map(row => (
            <React.Fragment key={row.label}>
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="min-w-0 break-words font-medium text-foreground">{row.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {display.href && (
        <div className="text-xs font-medium">
          <ReferenceLink href={display.href}>Open {sourceLabel}</ReferenceLink>
        </div>
      )}
      {linkedItems.length > 0 && (
        <div className="space-y-1.5">
          <InlineFieldLabel>Related Sources</InlineFieldLabel>
          <ul className="space-y-2">
            {linkedItems.map((artifact, index) => (
              <li key={`artifact-${index}`}>
                <ChangeArtifactDetail artifact={artifact} resolvers={resolvers ?? {}}/>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function EvidenceDetailFlyoutContent({
  evidence,
  open,
  onClose,
  title = "Sources",
  resolvers: initialResolvers,
  hydratedResolvers,
  loading,
}: EvidenceDetailFlyoutProps & {
  hydratedResolvers: EvidenceResolvers | undefined
  loading: boolean
}) {
  const resolvers = React.useMemo(
    () => mergeEvidenceResolvers(initialResolvers, hydratedResolvers),
    [initialResolvers, hydratedResolvers],
  )
  const items = React.useMemo(
    () => detailItems(evidence, resolvers),
    [evidence, resolvers],
  )
  if (items.length === 0) return null

  // First-seen order across rows so the header stack matches the row stacks.
  const providers = [...new Set(items.flatMap(item => item.providers))]
  const headerTitle = providers.length === 1 ? evidenceProviderLabel(providers[0]) : title
  const capturedAt = latestCapturedAt(items)
  const capturedText = capturedAt ? formatRelativeTime(capturedAt) : ""
  const referenceCount = items.length
  const meta = referenceCount > 1
    ? `${referenceCount} references${capturedText ? ` · latest ${capturedText}` : ""}`
    : capturedText ? `captured ${capturedText}` : "1 reference"

  return (
    <DetailFlyout
      open={open}
      onClose={onClose}
      title={headerTitle}
      icon={<HeaderSourcesIcon providers={providers}/>}
      meta={(
        <span className="inline-flex items-center gap-1.5">
          <span className="size-[5px] rounded-full bg-primary"/>
          <span>{meta}</span>
          {loading && <Loader2 className="size-3 animate-spin" aria-hidden/>}
        </span>
      )}
      ariaLabel="Close evidence details"
    >
      <div>
        {items.map((item, index) => (
          <EvidenceDetailRow
            key={`evidence-entry-${index}`}
            item={item}
            resolvers={resolvers}
          />
        ))}
      </div>
    </DetailFlyout>
  )
}

function detailItems(evidence: InsightEvidence[], resolvers: EvidenceResolvers | undefined): DetailItem[] {
  return displayableEvidence(evidence).map(item => {
    const providers = evidenceItemProviders(item, resolvers)
    return {
      evidence: item,
      display: toEvidenceDisplay(item, resolvers),
      provider: providers[0] ?? "unknown",
      providers,
    }
  })
}

function EvidenceDetailRow({
  item,
  resolvers,
  nested = false,
}: {
  item: DetailItem
  resolvers: EvidenceResolvers
  nested?: boolean
}) {
  const {display, provider, evidence} = item
  const note = display.note?.trim()
  const body = display.body?.trim()
  const reference = display.referenceText?.trim()
  const capturedAt = itemCapturedAt(item)
  const time = formatCompactRelativeTime(capturedAt)
  const metaRows = detailMetaRows(display)
  const showArtifacts = Boolean(display.linkedItems?.length)
  const nestedItems = nested ? [] : detailItems(display.nestedEvidence ?? [], resolvers)
  const signalRef = evidence.ref?.target
  const signal = signalRef?.$case === "signalId" ? resolvers.signals?.[signalRef.value] : undefined
  const signalArtifacts = (signal?.artifacts ?? []).filter(artifact => artifact.details?.$case !== "feature")
  const hasNestedEvidence = nestedItems.length > 0
  const hasDetails = Boolean(body) || metaRows.length > 0 || showArtifacts || signalArtifacts.length > 0 || hasNestedEvidence
  const [expanded, setExpanded] = React.useState(false)
  const [nestedExpanded, setNestedExpanded] = React.useState(false)

  const counters = artifactCounters(display.linkedItems)
  const notePreview = callNotePreviewTarget(evidence, resolvers)

  if (!expanded) {
    return (
      <CollapsedEvidenceDetailRow
        item={item}
        resolvers={resolvers}
        body={body}
        reference={reference}
        note={note}
        capturedAt={capturedAt}
        time={time}
        counters={counters}
        notePreview={notePreview}
        signalArtifacts={signalArtifacts}
        hasDetails={hasDetails}
        onExpand={() => setExpanded(true)}
      />
    )
  }

  return (
    <ExpandedEvidenceDetailRow
      item={item}
      resolvers={resolvers}
      body={body}
      reference={reference}
      note={note}
      capturedAt={capturedAt}
      time={time}
      metaRows={metaRows}
      counters={counters}
      notePreview={notePreview}
      hasDetails={hasDetails}
      linkedItems={display.linkedItems ?? []}
      signalArtifacts={signalArtifacts}
      nestedItems={nestedItems}
      nestedExpanded={nestedExpanded}
      onToggleNested={() => setNestedExpanded(value => !value)}
      onCollapse={() => setExpanded(false)}
    />
  )
}

function CollapsedEvidenceDetailRow({
  item,
  resolvers,
  body,
  reference,
  note,
  capturedAt,
  time,
  counters,
  notePreview,
  signalArtifacts,
  hasDetails,
  onExpand,
}: {
  item: DetailItem
  resolvers: EvidenceResolvers
  body: string | undefined
  reference: string | undefined
  note: string | undefined
  capturedAt: Date | undefined
  time: string
  counters: string[]
  notePreview: NotePreviewTarget | undefined
  signalArtifacts: SignalArtifact[]
  hasDetails: boolean
  onExpand: () => void
}) {
  const {display, provider} = item
  const title = note || reference || evidenceProviderLabel(provider)
  const description = body && body !== title ? body : undefined
  const rowLabel = aggregateEvidenceLabel(display) ?? evidenceProviderLabel(provider)
  const titleIsReference = Boolean(display.href && reference === title)

  return (
    <div className="py-1.5 first:pt-0 last:pb-0">
      <article className="overflow-hidden rounded-xl border bg-card shadow-xs transition-colors hover:border-foreground/15">
        <div className="flex items-start gap-3 p-4">
          <RowSourcesIcon providers={item.providers}/>
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px] uppercase leading-4 tracking-[0.14em] text-muted-foreground">
              <span>{rowLabel}</span>
              {time && (
                <span
                  title={capturedAt ? formatFullDate(capturedAt) : undefined}
                  className="normal-case tracking-normal text-muted-foreground/75"
                >
                  {time}
                </span>
              )}
            </div>
            <h3 className="text-[15px] font-semibold leading-[1.4] tracking-[-0.015em] text-foreground">
              {titleIsReference && display.href
                ? <ReferenceLink href={display.href}>{title}</ReferenceLink>
                : title}
            </h3>
            {description && (
              <div className="mt-1.5 line-clamp-3 text-[13px] leading-[1.55] text-muted-foreground">
                <Markdown content={description}/>
              </div>
            )}
          </div>
          {hasDetails && (
            <button
              type="button"
              onClick={onExpand}
              aria-expanded="false"
              aria-label="Show details"
              className="-m-2 inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronRight className="size-4" aria-hidden/>
            </button>
          )}
        </div>

        <EvidenceSourceFooter
          display={display}
          reference={reference}
          title={title}
          counters={counters}
          notePreview={notePreview}
          linkedItems={display.linkedItems ?? []}
          signalArtifacts={signalArtifacts}
          resolvers={resolvers}
        />
      </article>
    </div>
  )
}

function ExpandedEvidenceDetailRow({
  item,
  resolvers,
  body,
  reference,
  note,
  capturedAt,
  time,
  metaRows,
  counters,
  notePreview,
  hasDetails,
  linkedItems,
  signalArtifacts,
  nestedItems,
  nestedExpanded,
  onToggleNested,
  onCollapse,
}: {
  item: DetailItem
  resolvers: EvidenceResolvers
  body: string | undefined
  reference: string | undefined
  note: string | undefined
  capturedAt: Date | undefined
  time: string
  metaRows: DetailMetaRow[]
  counters: string[]
  notePreview: NotePreviewTarget | undefined
  hasDetails: boolean
  linkedItems: ResolvedChangeArtifact[]
  signalArtifacts: SignalArtifact[]
  nestedItems: DetailItem[]
  nestedExpanded: boolean
  onToggleNested: () => void
  onCollapse: () => void
}) {
  const {display, provider} = item
  const title = note || reference || evidenceProviderLabel(provider)
  const description = body && body !== title ? body : undefined
  const rowLabel = aggregateEvidenceLabel(display) ?? evidenceProviderLabel(provider)
  const titleIsReference = Boolean(display.href && reference === title)
  const hasNestedEvidence = nestedItems.length > 0
  const hasExpandedSections = metaRows.length > 0 || linkedItems.length > 0 || signalArtifacts.length > 0 || hasNestedEvidence

  return (
    <div className="py-1.5 first:pt-0 last:pb-0">
      <article className="overflow-hidden rounded-xl border bg-card shadow-xs transition-colors hover:border-foreground/15">
        <div className="flex items-start gap-3 p-4">
          <RowSourcesIcon providers={item.providers}/>
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[10px] uppercase leading-4 tracking-[0.14em] text-muted-foreground">
              <span>{rowLabel}</span>
              {time && (
                <span
                  title={capturedAt ? formatFullDate(capturedAt) : undefined}
                  className="normal-case tracking-normal text-muted-foreground/75"
                >
                  {time}
                </span>
              )}
            </div>
            <h3 className="text-[15px] font-semibold leading-[1.4] tracking-[-0.015em] text-foreground">
              {titleIsReference && display.href
                ? <ReferenceLink href={display.href}>{title}</ReferenceLink>
                : title}
            </h3>
            {description && (
              <div className="mt-1.5 text-[13px] leading-[1.55] text-muted-foreground">
                <Markdown content={description}/>
              </div>
            )}
          </div>
          {hasDetails && (
            <button
              type="button"
              onClick={onCollapse}
              aria-expanded="true"
              aria-label="Hide details"
              className="-m-2 inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <ChevronDown className="size-4" aria-hidden/>
            </button>
          )}
        </div>

        {hasExpandedSections && (
          <div className="space-y-3 border-t px-4 py-3 pl-[3.75rem]">
            {metaRows.length > 0 && (
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs">
                {metaRows.map(row => (
                  <React.Fragment key={row.label}>
                    <dt className="text-muted-foreground">{row.label}</dt>
                    <dd className="min-w-0 break-words font-medium text-foreground">{row.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}
            {linkedItems.length > 0 && (
              <div className="space-y-1.5">
                <InlineFieldLabel>Related Sources</InlineFieldLabel>
                <ul className="space-y-2">
                  {linkedItems.map((artifact, index) => (
                    <li key={`artifact-${index}`}>
                      <ChangeArtifactDetail artifact={artifact} resolvers={resolvers}/>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {signalArtifacts.length > 0 && (
              <div className="space-y-1.5">
                <InlineFieldLabel>Signal Sources</InlineFieldLabel>
                <ul className="space-y-2">
                  {signalArtifacts.map((artifact, index) => (
                    <li key={`signal-artifact-${index}`}>
                      <SignalArtifactRow artifact={artifact} resolvers={resolvers}/>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {hasNestedEvidence && (
              <div className="space-y-1.5">
                <button
                  type="button"
                  onClick={onToggleNested}
                  aria-expanded={nestedExpanded}
                  className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm text-left text-[10px] font-semibold uppercase leading-4 tracking-[0.12em] text-muted-foreground/75 underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {nestedExpanded
                    ? <ChevronDown className="size-3" aria-hidden/>
                    : <ChevronRight className="size-3" aria-hidden/>}
                  <span>Sources behind this insight</span>
                  <span className="font-mono tracking-normal text-muted-foreground/60">
                    {nestedItems.length}
                  </span>
                </button>
                {nestedExpanded && (
                  <div>
                    {nestedItems.map((nestedItem, index) => (
                      <EvidenceDetailRow
                        key={`nested-evidence-entry-${index}`}
                        item={nestedItem}
                        resolvers={resolvers}
                        nested
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <EvidenceSourceFooter
          display={display}
          reference={reference}
          title={title}
          counters={counters}
          notePreview={notePreview}
          linkedItems={linkedItems}
          signalArtifacts={signalArtifacts}
          resolvers={resolvers}
        />
      </article>
    </div>
  )
}

function aggregateEvidenceLabel(display: EvidenceDisplay): string | undefined {
  if (display.kind === "change") return "Change"
  if (display.kind === "signal") return "Signal"
  return undefined
}

function EvidenceSourceFooter({
  display,
  reference,
  title,
  counters,
  notePreview,
  linkedItems = [],
  signalArtifacts = [],
  resolvers,
}: {
  display: EvidenceDisplay
  reference: string | undefined
  title: string
  counters: string[]
  notePreview: NotePreviewTarget | undefined
  linkedItems?: ResolvedChangeArtifact[]
  signalArtifacts?: SignalArtifact[]
  resolvers?: EvidenceResolvers
}) {
  const showIdentity = Boolean(display.href && reference && reference !== title)
  const identityLabel = display.referenceLabel ?? display.label
  const linkedSourceItems = resolvers
    ? linkedItems.flatMap(artifact => {
      const identity = compactChangeSourceIdentity(artifact, resolvers)
      return identity ? [identity] : []
    })
    : []
  const signalSourceItems = resolvers
    ? signalArtifacts.flatMap(artifact => {
      const identity = compactSignalSourceIdentity(artifact, resolvers)
      return identity ? [identity] : []
    })
    : []
  const hasPrimarySourceRow = showIdentity || counters.length > 0 || Boolean(notePreview)

  if (linkedSourceItems.length === 0 && signalSourceItems.length === 0 && !hasPrimarySourceRow) return null

  return (
    <div className="space-y-2 border-t bg-muted/10 py-2.5 pl-[3.75rem] pr-4 text-[11px] leading-4 text-muted-foreground">
      {linkedSourceItems.map(source => (
        <CompactChangeSourceLink key={`${source.kind}-${source.identifier}`} source={source}/>
      ))}
      {signalSourceItems.map(source => (
        <CompactSignalSourceLink key={`${source.provider}-${source.href ?? source.identifier ?? source.label}`} source={source}/>
      ))}
      {hasPrimarySourceRow && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          {showIdentity && (
            <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
              <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground/75">
                {identityLabel}
              </span>
              <ReferenceLink href={display.href!} className="min-w-0 font-medium text-foreground/80">
                {reference}
              </ReferenceLink>
            </span>
          )}
          {notePreview && <span className="ml-auto"><NotePreviewButton preview={notePreview}/></span>}
          {counters.map(counter => (
            <span key={counter} className="font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground/80">{counter}</span>
          ))}
        </div>
      )}
    </div>
  )
}

interface CompactChangeSourceIdentity {
  kind: string
  label: string
  identifier: string
  href: string
}

function compactChangeSourceIdentity(
  artifact: ResolvedChangeArtifact,
  resolvers: EvidenceResolvers,
): CompactChangeSourceIdentity | undefined {
  const target = artifact.target
  if (!target) return undefined

  switch (target.$case) {
    case "pullRequest": {
      const {repo, number, title} = target.value
      const gitProvider = repoProvider(repo, resolvers.repoProviderMap)
      return {
        kind: "Pull request",
        label: title?.trim() || `${repoShortName(repo)}#${number}`,
        identifier: `${repoShortName(repo)}#${number}`,
        href: prUrl(repo, number, gitProvider),
      }
    }
    case "gitCommit": {
      const {repo, sha, message} = target.value
      const gitProvider = repoProvider(repo, resolvers.repoProviderMap)
      return {
        kind: "Commit",
        label: message?.trim() || `${repoShortName(repo)}@${sha.slice(0, 7)}`,
        identifier: `${repoShortName(repo)}@${sha.slice(0, 7)}`,
        href: commitUrl(repo, sha, gitProvider),
      }
    }
    case "issue": {
      const {project, id, title} = target.value
      const identifier = issueSourceIdentifier(project, id)
      const href = resolvers.tickets?.[issueEvidenceKey(target.value)]?.url
      if (!href) return undefined
      return {
        kind: "Issue",
        label: title?.trim() || identifier,
        identifier,
        href,
      }
    }
    default:
      return undefined
  }
}

function CompactChangeSourceLink({source}: {source: CompactChangeSourceIdentity}) {
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
      <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground/75">
        {source.kind}
      </span>
      <ReferenceLink href={source.href} className="min-w-0 font-medium text-foreground/80">
        {source.label}
      </ReferenceLink>
      {source.identifier !== source.label && (
        <span className="font-mono text-[10px] text-muted-foreground/75">{source.identifier}</span>
      )}
    </div>
  )
}

interface CompactSignalSourceIdentity {
  provider: EvidenceProvider
  label: string
  identifier?: string
  href?: string
}

function compactSignalSourceIdentity(
  artifact: SignalArtifact,
  resolvers: EvidenceResolvers,
): CompactSignalSourceIdentity | undefined {
  const issueIdentity = signalIssueSourceIdentity(artifact, resolvers)
  if (issueIdentity) return issueIdentity

  const artifactResolvers = {
    featureMap: resolvers.featureMap,
    callNotesByInternalId: resolvers.callNotesByInternalId,
    callNotesByProviderKey: resolvers.callNotesByProviderKey,
    documents: resolvers.documents,
    companyId: resolvers.companyId,
  }
  const baseHref = artifactReferenceHref(artifact, artifactResolvers)
  const href = artifact.details?.$case === "discussionTopic"
    ? slackHrefFromPermalink(baseHref, {nativeDeepLinks: resolvers.slackNativeDeepLinks})
    : baseHref
  if (!href) return undefined

  return {
    provider: artifactEvidenceProvider(artifact),
    label: artifactReferenceText(artifact, artifactResolvers).trim() || artifactProviderLabel(artifact),
    href,
  }
}

function CompactSignalSourceLink({source}: {source: CompactSignalSourceIdentity}) {
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
      <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground/75">
        {evidenceProviderLabel(source.provider)}
      </span>
      {source.href ? (
        <ReferenceLink href={source.href} className="min-w-0 font-medium text-foreground/80">
          {source.label}
        </ReferenceLink>
      ) : (
        <span className="min-w-0 break-words font-medium text-foreground/80">{source.label}</span>
      )}
      {source.identifier && source.identifier !== source.label && (
        <span className="font-mono text-[10px] text-muted-foreground/75">{source.identifier}</span>
      )}
    </div>
  )
}

function InlineFieldLabel({children}: {children: React.ReactNode}) {
  return (
    <span className="text-[10px] font-semibold uppercase leading-4 tracking-[0.12em] text-muted-foreground/75">
      {children}:
    </span>
  )
}

interface NotePreviewTarget {
  companyId: number
  providerType: string
  providerNoteId: string
  title: string
}

function NotePreviewButton({preview}: {preview: NotePreviewTarget}) {
  const [open, setOpen] = React.useState(false)
  const [record, setRecord] = React.useState<CallNoteRecord | undefined>()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | undefined>()

  const loadPreview = React.useCallback(async () => {
    if (record || loading) return
    setLoading(true)
    setError(undefined)
    const body = GetCallNoteByProviderRefRequest.toJSON({
      providerType: preview.providerType,
      noteId: preview.providerNoteId,
    })
    try {
      const js = await fetch(companyCallNoteByProviderRefAPI(preview.companyId), {
        method: "POST",
        headers: {"content-type": "application/json"},
        body: JSON.stringify(body),
      })
        .then(jr)
      const response = GetCallNoteByProviderRefResponse.fromJSON(js)
      setRecord(response.callNoteJson ? CallNoteRecord.fromJSON(JSON.parse(response.callNoteJson)) : undefined)
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      } else if (typeof err === "string") {
        setError(err)
      } else {
        setError("Failed to load note")
      }
    } finally {
      setLoading(false)
    }
  }, [loading, preview.providerNoteId, preview.providerType, preview.companyId, record])

  const title = record?.title?.trim() || preview.title || "Meeting note"
  const summary = record?.summaryMarkdown?.trim()

  return (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setOpen(true)
          void loadPreview()
        }}
        aria-label={`Preview ${title}`}
        title="Preview note"
        className="inline-flex size-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
      >
        <Eye className="size-3.5" aria-hidden/>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[82vh] flex-col overflow-hidden sm:max-w-2xl">
          <DialogHeader className="shrink-0 pr-6">
            <DialogTitle className="text-base leading-6">{title}</DialogTitle>
            <DialogDescription>
              Call note
            </DialogDescription>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto pr-1">
            {loading ? (
              <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden/>
                Loading note
              </div>
            ) : error ? (
              <p className="py-4 text-sm text-destructive">{error}</p>
            ) : summary ? (
              <div className="prose prose-sm max-w-none text-[13px] leading-6 dark:prose-invert">
                <Markdown content={summary}/>
              </div>
            ) : (
              <p className="py-4 text-sm text-muted-foreground">No note content is available.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

function callNotePreviewTarget(evidence: InsightEvidence, resolvers: EvidenceResolvers): NotePreviewTarget | undefined {
  const companyId = resolvers.companyId
  const target = evidence.ref?.target
  if (companyId === undefined || !target) return undefined
  if (target.$case === "callNoteId") {
    const note = resolvers.callNotesByInternalId?.[target.value]
    return notePreviewFromMetadata(companyId, note, callNoteTitle(note))
  }
  if (target.$case === "callNote") {
    const note = resolvers.callNotesByProviderKey?.[callNoteProviderKey(target.value.providerType, target.value.noteId)]
    return notePreviewFromMetadata(companyId, note, callNoteTitle(note) || target.value.noteId)
  }
  return undefined
}

function signalArtifactNotePreviewTarget(artifact: SignalArtifact, resolvers: EvidenceResolvers): NotePreviewTarget | undefined {
  const companyId = resolvers.companyId
  const details = artifact.details
  if (companyId === undefined || details?.$case !== "callNote") return undefined

  const note = resolvers.callNotesByProviderKey?.[callNoteProviderKey(details.value.providerType, details.value.noteId)]
  return notePreviewFromMetadata(companyId, note, details.value.title.trim())
}

function notePreviewFromMetadata(
  companyId: number,
  note: CallNoteMetadata | undefined,
  fallbackTitle: string,
): NotePreviewTarget | undefined {
  if (!note || !note.providerType || !note.providerNoteId) return undefined
  return {
    companyId,
    providerType: note.providerType,
    providerNoteId: note.providerNoteId,
    title: fallbackTitle || callNoteTitle(note) || "Call note",
  }
}

const MAX_HEADER_STACKED_ICONS = 3

/** Single source → its brand mark; mixed sources → stacked provider tiles. */
function HeaderSourcesIcon({providers}: {providers: EvidenceProvider[]}) {
  if (providers.length <= 1) {
    return <EvidenceProviderGlyph provider={providers[0] ?? "unknown"} className="size-6"/>
  }
  return <StackedSourceIcons providers={providers}/>
}

/** Overlapping run of round provider tiles with a "+N" overflow badge. */
function StackedSourceIcons({providers}: {providers: EvidenceProvider[]}) {
  const shown = providers.slice(0, MAX_HEADER_STACKED_ICONS)
  const hidden = providers.length - shown.length
  return (
    <span className="flex shrink-0 items-center">
      {shown.map((provider, index) => (
        <span key={provider} className={cn("relative overflow-hidden rounded-full ring-2 ring-background", index > 0 && "-ml-3")}>
          <EvidenceProviderIcon provider={provider} size="sm"/>
        </span>
      ))}
      {hidden > 0 && (
        <span className="relative -ml-3 inline-flex size-7 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-2 ring-background">
          +{hidden}
        </span>
      )}
    </span>
  )
}

function ReferenceLink({href, className, children}: {href: string; className?: string; children: React.ReactNode}) {
  const external = isExternalReferenceHref(href)
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      className={cn("inline-flex max-w-full items-center gap-0.5 underline-offset-2 hover:underline", className)}
    >
      <span className="truncate">{children}</span>
      {external && <ArrowUpRight className="size-3 shrink-0 text-muted-foreground" aria-hidden/>}
    </a>
  )
}

function isExternalReferenceHref(href: string | undefined): boolean {
  return Boolean(href?.startsWith("http://") || href?.startsWith("https://") || href?.startsWith("slack://"))
}

/** One source → its tile; aggregate refs (changes, signals) → stacked tiles
 * of their underlying sources. */
function RowSourcesIcon({providers}: {providers: EvidenceProvider[]}) {
  if (providers.length <= 1) {
    return <EvidenceProviderIcon provider={providers[0] ?? "unknown"} size="sm"/>
  }
  return <StackedSourceIcons providers={providers}/>
}

/** One connected change artifact: origin link plus its `contribution` —
 * the model's 1-2 sentence explanation of how it relates to the change. */
function ChangeArtifactDetail({artifact, resolvers}: {artifact: ResolvedChangeArtifact; resolvers: EvidenceResolvers}) {
  const target = artifact.target
  if (!target) return null

  let label = ""
  let identifier = ""
  let href: string | undefined
  let provider: EvidenceProvider = "unknown"
  let featureRelationshipLabel = ""
  switch (target.$case) {
    case "pullRequest": {
      const {repo, number, title} = target.value
      identifier = `${repoShortName(repo)}#${number}`
      label = title?.trim() || identifier
      const gitProvider = repoProvider(repo, resolvers.repoProviderMap)
      href = prUrl(repo, number, gitProvider)
      provider = gitProviderEvidenceProvider(gitProvider) ?? "github"
      break
    }
    case "gitCommit": {
      const {repo, sha, message} = target.value
      identifier = `${repoShortName(repo)}@${sha.slice(0, 7)}`
      label = message?.trim() || identifier
      const gitProvider = repoProvider(repo, resolvers.repoProviderMap)
      href = commitUrl(repo, sha, gitProvider)
      provider = gitProviderEvidenceProvider(gitProvider) ?? "github"
      break
    }
    case "issue": {
      const {project, id, title, tracker} = target.value
      identifier = issueSourceIdentifier(project, id)
      label = title?.trim() || identifier
      href = resolvers.tickets?.[issueEvidenceKey(target.value)]?.url || undefined
      provider = tracker === IssueTracker.ISSUE_TRACKER_JIRA
        ? "jira"
        : tracker === IssueTracker.ISSUE_TRACKER_LINEAR ? "linear" : "ticket"
      break
    }
    case "feature": {
      const {featureId, relationship} = target.value
      const feature = resolvers.featureMap?.get(featureId)
      label = catalogueFeatureLabel(feature) ?? "Feature"
      href = catalogueFeatureHref(resolvers.companyId, featureId)
      provider = "feature"
      featureRelationshipLabel = FEATURE_RELATIONSHIP_LABELS[relationship] ?? ""
      break
    }
  }
  if (!label) return null

  const contribution = artifact.contribution?.trim()
  if (target.$case === "feature") {
    return (
      <div className="min-w-0 space-y-0.5">
        <FeatureArtifactBadge
          label={label}
          relationshipLabel={featureRelationshipLabel}
          href={href}
        />
        {contribution && <p className="text-xs leading-5 text-muted-foreground">{contribution}</p>}
      </div>
    )
  }

  const showIdentifier = Boolean(identifier) && identifier !== label
  const line = (
    <>
      <span className="text-xs font-medium text-foreground/90">{label}</span>
      {showIdentifier && (
        <span className="font-mono text-[11px] text-muted-foreground/70">{identifier}</span>
      )}
    </>
  )
  const external = isExternalReferenceHref(href)
  return (
    <div className="flex items-start gap-2">
      <ArtifactGlyph provider={provider}/>
      <div className="min-w-0 flex-1 space-y-0.5">
        {href ? (
          <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noreferrer" : undefined}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 underline-offset-2 hover:underline"
          >
            {line}
            {external && <ArrowUpRight className="size-3 shrink-0 self-center text-muted-foreground" aria-hidden/>}
          </a>
        ) : (
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">{line}</span>
        )}
        {contribution && <p className="text-xs leading-5 text-muted-foreground">{contribution}</p>}
      </div>
    </div>
  )
}

function FeatureArtifactBadge({
  label,
  relationshipLabel,
  href,
}: {
  label: string
  relationshipLabel: string
  href: string | undefined
}) {
  const className = "inline-flex max-w-full items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-xs text-blue-600 transition-colors hover:bg-blue-500/20 dark:text-blue-400"
  const content = (
    <>
      <Box className="size-3 shrink-0" aria-hidden/>
      <span className="break-words whitespace-normal">{label}</span>
      {relationshipLabel && <span className="text-blue-400 dark:text-blue-500">· {relationshipLabel}</span>}
    </>
  )

  if (!href) {
    return <span className={className}>{content}</span>
  }

  return (
    <a href={href} className={className}>
      {content}
    </a>
  )
}

/** "org/repo" → "repo"; display only — links keep the full path. */
function repoShortName(repo: string): string {
  const trimmed = repo.trim()
  const slash = trimmed.lastIndexOf("/")
  return slash >= 0 ? trimmed.slice(slash + 1) : trimmed
}

function issueSourceIdentifier(project: string, id: string): string {
  const trimmedProject = project.trim()
  const trimmedId = id.trim()
  if (!trimmedProject || trimmedId.toUpperCase().startsWith(`${trimmedProject.toUpperCase()}-`)) return trimmedId
  return `${trimmedProject}-${trimmedId}`
}

/** One signal artifact: source link (call, chat thread, ticket) plus
 * its `relevance` — why the artifact relates to the signal. */
function SignalArtifactRow({artifact, resolvers}: {artifact: SignalArtifact; resolvers: EvidenceResolvers}) {
  const provider = artifactEvidenceProvider(artifact)
  const artifactResolvers = {
    featureMap: resolvers.featureMap,
    callNotesByInternalId: resolvers.callNotesByInternalId,
    callNotesByProviderKey: resolvers.callNotesByProviderKey,
    documents: resolvers.documents,
    companyId: resolvers.companyId,
  }
  const issueIdentity = signalIssueSourceIdentity(artifact, resolvers)
  const label = issueIdentity?.label ?? (artifactReferenceText(artifact, artifactResolvers).trim() || artifactProviderLabel(artifact))
  const identifier = issueIdentity?.identifier
  const baseHref = artifactReferenceHref(artifact, artifactResolvers)
  const href = issueIdentity?.href ?? (discussionTopicArtifactProvider(artifact) === "slack"
    ? slackHrefFromPermalink(baseHref, {nativeDeepLinks: resolvers.slackNativeDeepLinks})
    : baseHref)
  if (!label) return null

  const relevance = artifact.relevance?.trim()
  const notePreview = signalArtifactNotePreviewTarget(artifact, resolvers)
  return (
    <div className="flex items-start gap-2">
      <ArtifactGlyph provider={provider}/>
      <div className="min-w-0 flex-1 space-y-0.5">
        {href ? (
          <span className="inline-flex items-center gap-1">
            <ReferenceLink href={href} className="text-xs font-medium text-foreground/90">{label}</ReferenceLink>
            {notePreview && <NotePreviewButton preview={notePreview}/>}
          </span>
        ) : (
          <span className="text-xs font-medium text-foreground/90">{label}</span>
        )}
        {identifier && identifier !== label && (
          <span className="font-mono text-[11px] text-muted-foreground/70">{identifier}</span>
        )}
        {relevance && <p className="text-xs leading-5 text-muted-foreground">{relevance}</p>}
      </div>
    </div>
  )
}

function signalIssueSourceIdentity(
  artifact: SignalArtifact,
  resolvers: EvidenceResolvers,
): CompactSignalSourceIdentity | undefined {
  const details = artifact.details
  if (details?.$case !== "issue") return undefined

  const issue = {
    tracker: details.value.tracker,
    project: "",
    id: details.value.issueId,
    title: details.value.title,
  }
  const ticket = resolvers.tickets?.[issueEvidenceKey(issue)]
  const identifier = ticket?.summary?.id?.trim() || details.value.issueId.trim()
  const label = details.value.title.trim() || ticket?.summary?.title?.trim() || identifier || artifactProviderLabel(artifact)

  return {
    provider: artifactEvidenceProvider(artifact),
    label,
    identifier,
    href: ticket?.url?.trim() || undefined,
  }
}

function ArtifactGlyph({provider}: {provider: EvidenceProvider}) {
  return (
    <span className="flex w-3 shrink-0 items-center justify-center pt-2">
      <EvidenceProviderGlyph provider={provider} className="size-3"/>
    </span>
  )
}

/** Visible-at-a-glance counts of a change's connected artifacts ("3 PRs"). */
function artifactCounters(artifacts: ResolvedChangeArtifact[] | undefined): string[] {
  if (!artifacts?.length) return []
  let prs = 0
  let commits = 0
  let issues = 0
  let features = 0
  for (const artifact of artifacts) {
    switch (artifact.target?.$case) {
      case "pullRequest":
        prs += 1
        break
      case "gitCommit":
        commits += 1
        break
      case "issue":
        issues += 1
        break
      case "feature":
        features += 1
        break
    }
  }
  const out: string[] = []
  if (prs > 0) out.push(`${prs} PR${prs === 1 ? "" : "s"}`)
  if (commits > 0) out.push(`${commits} commit${commits === 1 ? "" : "s"}`)
  if (issues > 0) out.push(`${issues} issue${issues === 1 ? "" : "s"}`)
  if (features > 0) out.push(`${features} feature${features === 1 ? "" : "s"}`)
  return out
}

interface DetailMetaRow {
  label: string
  value: string
}

function detailMetaRows(display: EvidenceDisplay): DetailMetaRow[] {
  const rows: DetailMetaRow[] = []
  rows.push(...(display.attributes ?? []))
  const people = display.people?.trim()
  if (people) rows.push({label: display.kind === "issue" ? "Assignee" : "People", value: people})
  if (display.status) rows.push({label: "Status", value: display.status})
  return rows
}

function formatFullDate(date: Date): string {
  return date.toLocaleDateString(undefined, {year: "numeric", month: "long", day: "numeric"})
}

/** Evidence wrapper timestamp, falling back to when the source entity itself
 * was captured (change/signal/call-note timestamps arrive via hydration). */
function itemCapturedAt(item: {evidence: InsightEvidence; display: EvidenceDisplay}): Date | undefined {
  return capturedDate(item.evidence.createdAt) ?? capturedDate(item.display.occurredAt)
}

function latestCapturedAt(items: Array<{evidence: InsightEvidence; display: EvidenceDisplay}>): Date | undefined {
  let latest: Date | undefined
  for (const item of items) {
    const date = itemCapturedAt(item)
    if (!date) continue
    if (!latest || date.getTime() > latest.getTime()) latest = date
  }
  return latest
}

/** `createdAt` can arrive as an ISO string when evidence skipped proto JSON
 * conversion — normalize before formatting. */
function capturedDate(value: Date | string | undefined): Date | undefined {
  if (!value) return undefined
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date
}
